import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2, Download, RefreshCw, Copy, Check, Instagram, Facebook } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAMPAIGN_CODE = 'LAUNCH20';

export default function AdminSocialPage() {
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [captions, setCaptions] = useState(null);
  const [loadingCaptions, setLoadingCaptions] = useState(false);
  const [copied, setCopied] = useState(null);
  const [imageVersion, setImageVersion] = useState(0); // cache-buster for the previews

  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => {
      setProducts(data || []);
      if (data?.length) setSelectedId(data[0].id);
    });
  }, []);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedId) || null,
    [products, selectedId],
  );

  // Auto-load any cached captions when switching products
  useEffect(() => {
    if (!selectedId) return;
    setCaptions(null);
    setLoadingCaptions(true);
    axios
      .get(`${API}/admin/social/${selectedId}/captions`)
      .then(({ data }) => setCaptions(data))
      .catch(() => setCaptions(null))
      .finally(() => setLoadingCaptions(false));
  }, [selectedId]);

  const regenerate = async () => {
    if (!selectedId) return;
    setLoadingCaptions(true);
    setCaptions(null);
    try {
      const { data } = await axios.get(
        `${API}/admin/social/${selectedId}/captions?regenerate=true`,
      );
      setCaptions(data);
      toast.success('Fresh captions generated');
    } catch (_e) {
      toast.error('Could not generate right now — try again in a moment');
    } finally {
      setLoadingCaptions(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success('Copied');
      setTimeout(() => setCopied(null), 1500);
    } catch (_e) {
      toast.error('Copy failed — select the text manually');
    }
  };

  const igCaptionFull = useMemo(() => {
    if (!captions) return '';
    const tags = (captions.hashtags || []).join(' ');
    return `${captions.instagram_caption}\n\n.\n.\n.\n${tags}`;
  }, [captions]);

  const fbCaptionFull = captions?.facebook_caption || '';

  const imageUrl = (fmt) =>
    selectedId
      ? `${API}/admin/social/${selectedId}/image?fmt=${fmt}&v=${imageVersion}`
      : null;

  const refreshImages = () => setImageVersion((v) => v + 1);

  return (
    <div className="pt-28 pb-20 min-h-screen" data-testid="admin-social-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-sm text-[#84A98C] hover:text-[#354F52] mb-6"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="uppercase tracking-widest">Back to admin</span>
          </Link>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <p className="text-caption text-[#84A98C] mb-3">Social media kit</p>
              <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-3">
                Pick a product. Get a post.
              </h1>
              <p className="text-[#52796F] max-w-2xl">
                Generate a ready-to-post Instagram + Facebook caption, hashtags, and on-brand
                visuals for any product — carrying your{' '}
                <strong className="text-[#D4AF37]">{CAMPAIGN_CODE}</strong> (20% off) launch code.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          {/* Product picker */}
          <aside data-testid="product-picker">
            <p className="text-xs uppercase tracking-widest text-[#84A98C] mb-3">Choose a product</p>
            <div className="space-y-1 max-h-[600px] overflow-y-auto pr-2">
              {products.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    selectedId === p.id
                      ? 'bg-[#52796F] text-[#FAF9F6]'
                      : 'bg-[#F5F5F0] text-[#354F52] hover:bg-[#CAD2C5]/40'
                  }`}
                  data-testid={`product-option-${p.id}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </aside>

          {/* Right panel */}
          <section>
            {!selectedProduct ? (
              <div className="text-sm text-[#84A98C]">Pick a product to begin.</div>
            ) : (
              <div className="space-y-10">
                {/* Selected product summary */}
                <div className="flex items-center gap-4 p-4 bg-[#F5F5F0] rounded-sm">
                  {selectedProduct.image && (
                    <img
                      src={
                        selectedProduct.image.startsWith('/api/')
                          ? `${process.env.REACT_APP_BACKEND_URL}${selectedProduct.image}`
                          : selectedProduct.image
                      }
                      alt={selectedProduct.name}
                      className="w-20 h-20 object-cover rounded-sm"
                    />
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#84A98C]">Now creating</p>
                    <p className="font-serif text-xl text-[#354F52]">{selectedProduct.name}</p>
                    <p className="text-xs text-[#52796F]">
                      Morse · {selectedProduct.morse_text || '—'}
                    </p>
                  </div>
                </div>

                {/* Images */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs uppercase tracking-widest text-[#84A98C]">
                      Ready-to-post visuals
                    </p>
                    <button
                      onClick={refreshImages}
                      className="text-xs text-[#84A98C] hover:text-[#354F52] flex items-center gap-1"
                      data-testid="refresh-images"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Reload
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {/* Square */}
                    <div>
                      <div className="aspect-square bg-[#F5F5F0] rounded-sm overflow-hidden mb-3">
                        {selectedId && (
                          <img
                            key={`sq-${selectedId}-${imageVersion}`}
                            src={imageUrl('square')}
                            alt="Square social post"
                            className="w-full h-full object-cover"
                            data-testid="preview-square"
                          />
                        )}
                      </div>
                      <a
                        href={imageUrl('square')}
                        download={`ww-${selectedId}-square.png`}
                        className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm bg-[#52796F] text-[#FAF9F6] hover:bg-[#354F52] rounded-sm transition-colors"
                        data-testid="download-square"
                      >
                        <Download className="w-4 h-4" />
                        Square 1080² (IG/FB feed)
                      </a>
                    </div>

                    {/* Story */}
                    <div>
                      <div className="aspect-[9/16] bg-[#F5F5F0] rounded-sm overflow-hidden mb-3 max-h-[420px] mx-auto">
                        {selectedId && (
                          <img
                            key={`st-${selectedId}-${imageVersion}`}
                            src={imageUrl('story')}
                            alt="Story social post"
                            className="w-full h-full object-cover"
                            data-testid="preview-story"
                          />
                        )}
                      </div>
                      <a
                        href={imageUrl('story')}
                        download={`ww-${selectedId}-story.png`}
                        className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm bg-[#52796F] text-[#FAF9F6] hover:bg-[#354F52] rounded-sm transition-colors"
                        data-testid="download-story"
                      >
                        <Download className="w-4 h-4" />
                        Story 1080×1920 (IG/FB)
                      </a>
                    </div>

                    {/* Pinterest */}
                    <div>
                      <div className="aspect-[2/3] bg-[#F5F5F0] rounded-sm overflow-hidden mb-3 max-h-[420px] mx-auto">
                        {selectedId && (
                          <img
                            key={`pt-${selectedId}-${imageVersion}`}
                            src={imageUrl('pinterest')}
                            alt="Pinterest pin"
                            className="w-full h-full object-cover"
                            data-testid="preview-pinterest"
                          />
                        )}
                      </div>
                      <a
                        href={imageUrl('pinterest')}
                        download={`ww-${selectedId}-pinterest.png`}
                        className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm bg-[#BC4749] text-[#FAF9F6] hover:bg-[#9c3a3c] rounded-sm transition-colors"
                        data-testid="download-pinterest"
                      >
                        <Download className="w-4 h-4" />
                        Pinterest 1000×1500
                      </a>
                    </div>
                  </div>
                </div>

                {/* Captions */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs uppercase tracking-widest text-[#84A98C]">Captions</p>
                    <Button
                      onClick={regenerate}
                      disabled={loadingCaptions}
                      variant="outline"
                      size="sm"
                      data-testid="regenerate-captions"
                      className="text-xs gap-1.5"
                    >
                      <RefreshCw
                        className={`w-3 h-3 ${loadingCaptions ? 'animate-spin' : ''}`}
                      />
                      {loadingCaptions ? 'Writing…' : 'Regenerate'}
                    </Button>
                  </div>

                  {loadingCaptions && !captions && (
                    <div className="text-sm text-[#84A98C] italic">
                      Lindy's ghostwriter is thinking…
                    </div>
                  )}

                  {captions && (
                    <div className="space-y-6">
                      {/* Instagram */}
                      <div className="border border-[#CAD2C5]/60 rounded-sm overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#F5F5F0] border-b border-[#CAD2C5]/40">
                          <div className="flex items-center gap-2 text-sm text-[#354F52]">
                            <Instagram className="w-4 h-4" />
                            <span className="font-medium">Instagram caption + hashtags</span>
                          </div>
                          <button
                            onClick={() => copy(igCaptionFull, 'ig')}
                            className="text-xs text-[#84A98C] hover:text-[#354F52] flex items-center gap-1"
                            data-testid="copy-ig-caption"
                          >
                            {copied === 'ig' ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                            {copied === 'ig' ? 'Copied' : 'Copy all'}
                          </button>
                        </div>
                        <pre
                          className="px-4 py-4 text-sm text-[#354F52] whitespace-pre-wrap font-sans leading-relaxed"
                          data-testid="ig-caption-text"
                        >
                          {igCaptionFull}
                        </pre>
                      </div>

                      {/* Facebook */}
                      <div className="border border-[#CAD2C5]/60 rounded-sm overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#F5F5F0] border-b border-[#CAD2C5]/40">
                          <div className="flex items-center gap-2 text-sm text-[#354F52]">
                            <Facebook className="w-4 h-4" />
                            <span className="font-medium">Facebook caption</span>
                          </div>
                          <button
                            onClick={() => copy(fbCaptionFull, 'fb')}
                            className="text-xs text-[#84A98C] hover:text-[#354F52] flex items-center gap-1"
                            data-testid="copy-fb-caption"
                          >
                            {copied === 'fb' ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                            {copied === 'fb' ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                        <pre
                          className="px-4 py-4 text-sm text-[#354F52] whitespace-pre-wrap font-sans leading-relaxed"
                          data-testid="fb-caption-text"
                        >
                          {fbCaptionFull}
                        </pre>
                      </div>

                      {captions.model_used && (
                        <p className="text-[10px] tracking-widest uppercase text-[#84A98C]">
                          Drafted with {captions.model_used}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Posting workflow */}
                <div className="p-5 bg-[#F5F5F0] rounded-sm">
                  <div className="flex items-start gap-3">
                    <Share2 className="w-4 h-4 text-[#D4AF37] mt-1" strokeWidth={1.5} />
                    <div className="text-sm text-[#52796F] leading-relaxed">
                      <p className="font-medium text-[#354F52] mb-2">How to post</p>
                      <ol className="list-decimal pl-5 space-y-1">
                        <li>Download the feed image → upload to Instagram or Facebook as a normal post.</li>
                        <li>Paste the matching caption.</li>
                        <li>Download the story image → share to Instagram or Facebook Stories.</li>
                        <li>Pin "Link in bio: whisperingwristwear.com" to your profile.</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
